// @format

type Post = (url: string, data: any, success: (response: any) => void, complete?: () => void) => void;
type StateOption = {state: string; label: string; description?: string};
type ForwardDetail = {recipients: string[]; at: string; by?: string | null; note?: string | null};

const TRIGGERS = ".inbox-state-toggle, .inbox-drawer-state";
const GAP_PX = 6;
const EDGE_PX = 8;

let $menu: JQuery | null = null;
let $trigger: JQuery | null = null;
let windowBound = false;

function closeMenu(refocus = false) {
  if (!$menu) return;

  $menu.remove();
  $menu = null;
  if ($trigger) {
    $trigger.removeClass("open").attr("aria-expanded", "false");
    if (refocus) $trigger.trigger("focus");
  }
  $trigger = null;
}

// Below the flag, or above it when there is no room underneath; never past the viewport's edge.
function placeMenu() {
  if (!$menu || !$trigger) return;

  const rect = ($trigger.get(0) as HTMLElement).getBoundingClientRect();
  const menu = $menu.get(0) as HTMLElement;
  const below = rect.bottom + GAP_PX;
  const top = below + menu.offsetHeight > window.innerHeight - EDGE_PX
    ? Math.max(EDGE_PX, rect.top - GAP_PX - menu.offsetHeight)
    : below;
  const left = Math.max(EDGE_PX, Math.min(rect.left, window.innerWidth - menu.offsetWidth - EDGE_PX));

  $menu.css({top, left});
}

function optionsFor($button: JQuery): StateOption[] {
  const options = $button.data("options");
  return Array.isArray(options) ? options : [];
}

function detailsFor($button: JQuery): ForwardDetail[] {
  const details = $button.data("details");
  return Array.isArray(details) ? details : [];
}

function openDetails($button: JQuery) {
  closeMenu();

  $trigger = $button.addClass("open").attr("aria-expanded", "true");
  $menu = $("<div>", {class: "inbox-state-menu inbox-state-details", role: "dialog", "aria-label": "Forwarded", tabindex: "-1"});
  $("<div>", {class: "inbox-state-menu-head", text: "Forwarded"}).appendTo($menu);

  const details = detailsFor($button);
  if (!details.length) {
    $("<div>", {class: "inbox-state-menu-desc inbox-state-detail", text: "Marked forwarded by hand"}).appendTo($menu);
  }
  details.forEach(({recipients, at, by, note}) => {
    $("<div>", {class: "inbox-state-detail"}).append(
      $("<span>", {class: "inbox-state-menu-label", text: recipients.join(", ")}),
      $("<span>", {class: "inbox-state-menu-desc", text: by ? `${at} · ${by}` : at}),
      note ? $("<span>", {class: "inbox-state-menu-desc inbox-state-detail-note", text: note}) : $()
    ).appendTo($menu as JQuery);
  });

  $menu.appendTo(document.body);
  placeMenu();
  ($menu.get(0) as HTMLElement).focus({preventScroll: true});

  $menu.on("keydown", function(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenu(true);
    } else if (event.key === "Tab") {
      closeMenu();
    }
  });
}

function openMenu($button: JQuery, post: Post) {
  closeMenu();

  const current = String($button.data("state"));
  $trigger = $button.addClass("open").attr("aria-expanded", "true");
  $menu = $("<div>", {class: "inbox-state-menu", role: "menu", "aria-label": "Response"});
  $("<div>", {class: "inbox-state-menu-head", "aria-hidden": "true", text: "Response"}).appendTo($menu);

  optionsFor($button).forEach(({state, label, description}) => {
    const active = state === current;
    $("<button>", {
      type: "button",
      role: "menuitemradio",
      class: `inbox-state-menu-opt ${state}${active ? " active" : ""}`,
      "aria-checked": String(active),
      "data-state": state
    }).append(
      $("<span>", {class: "inbox-state-menu-text"}).append(
        $("<span>", {class: "inbox-state-menu-label", text: label}),
        description ? $("<span>", {class: "inbox-state-menu-desc", text: description}) : $()
      )
    ).appendTo($menu as JQuery);
  });

  $menu.appendTo(document.body);
  placeMenu();
  const first = ($menu.find(".active").get(0) || $menu.find(".inbox-state-menu-opt").get(0)) as HTMLElement | undefined;
  // preventScroll: a scroll would trip the listener below and close the menu it just opened.
  first?.focus({preventScroll: true});

  $menu.on("click", ".inbox-state-menu-opt", function(event) {
    event.stopPropagation();
    const state = String($(this).data("state"));
    const $flag = $trigger as JQuery;
    closeMenu(true);
    if (state === current) return;

    $flag.prop("disabled", true);
    post(String($flag.data("url")), {state}, () => window.location.reload(), () => $flag.prop("disabled", false));
  });

  $menu.on("keydown", function(event) {
    const $options = ($menu as JQuery).find(".inbox-state-menu-opt");
    const index = $options.index(document.activeElement as HTMLElement);

    if (event.key === "Escape") {
      // The page's own Escape closes the drawer; this one only closes the menu.
      event.preventDefault();
      event.stopPropagation();
      closeMenu(true);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      ($options.get((index + step + $options.length) % $options.length) as HTMLElement).focus({preventScroll: true});
    } else if (event.key === "Tab") {
      closeMenu();
    }
  });
}

export function initInboxStateMenu($page: JQuery, post: Post) {
  // A Turbo visit swaps <body>, taking a menu left open on the last page with it.
  closeMenu();

  $page.off("click.inboxState").on("click.inboxState", TRIGGERS, function(event) {
    event.stopPropagation();
    if ($trigger && $trigger.get(0) === this) {
      closeMenu();
      return;
    }

    const $button = $(this);
    if ($button.data("locked")) openDetails($button);
    else openMenu($button, post);
  });

  $(document).off("mousedown.inboxStateMenu").on("mousedown.inboxStateMenu", (event) => {
    if (!$menu) return;

    const $target = $(event.target);
    if ($target.closest(".inbox-state-menu").length) return;
    if ($trigger && $target.closest($trigger).length) return;
    closeMenu();
  });

  if (windowBound) return;
  windowBound = true;
  // The menu is fixed to where the flag was; once the page moves under it, it points at nothing.
  window.addEventListener("scroll", () => closeMenu(), true);
  window.addEventListener("resize", () => closeMenu());
}
